import React, { useEffect, useState, useRef } from "react";
import { X, Check, Repeat } from "react-feather";
import { apiService } from "../../../../services/apiService.ts";
import StaffPinModal from "../Common/StaffPinModal.jsx";

export default function Arrivals({ isOpen, onClose, booking }) {
  const [accountName, setAccountName] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [guests, setGuests] = useState([]);
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [mainHolderId, setMainHolderId] = useState(null);
  const [showMainHolderModal, setShowMainHolderModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  const [staffPinModal, setStaffPinModal] = useState(false);
  const [, setIsSubmitting] = useState(false);
  const [, setPinError] = useState(false);

  // Load accounts
  useEffect(() => {
    if (!isOpen) return;
    apiService.getAccounts().then((res) => {
      if (res.Success) setAccounts(res.Data);
    });
  }, [isOpen]);

  // Load guests for the booking
  useEffect(() => {
    if (!isOpen || !booking?.BookingHeaderID) return;

    apiService
      .getCheckInBookingGuests(
        booking.BookingHeaderID,
        selectedAccount ? selectedAccount.AccountID : null
      )
      .then((res) => {
        if (res.Success) setGuests(res.Data || []);
        else setGuests([]);
      });
  }, [isOpen, booking, selectedAccount]);

  // Close account dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleGuest = (id) => {
    setSelectedGuestIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAccountSelect = (acc) => {
    setAccountName(acc.Name);
    setSelectedAccount(acc);
    setDropdownOpen(false);
    setSelectedGuestIds([]);
  };

  // Continue button → choose main holder
  const handleCheckIn = () => {
    if (selectedGuestIds.length === 0) {
      setSelectedGuestIds(guests.map((g) => g.GuestID));
    }
    setShowMainHolderModal(true);
  };

  // Submit after staff PIN approval
  const handleSubmitCheckInWithStaff = async (staffId) => {
    try {
      setIsSubmitting(true);
      setPinError(false);

      let acc = selectedAccount;

      // If account does not exist → create new
      if (!acc) {
        const newAccResp = await apiService.addAccount({
          Name: accountName,
          FK_BookingID: booking.BookingHeaderID,
          FK_ResponsibleID: mainHolderId,
        });

        if (newAccResp.Success && newAccResp.Data?.AccountID) {
          acc = {
            AccountID: newAccResp.Data.AccountID,
            Name: accountName,
          };
          setSelectedAccount(acc);
        } else {
          alert("Failed to create account");
          return;
        }
      }

      const payload = selectedGuestIds.map((id) => ({
        GuestID: id,
        IsResponsible:
          selectedGuestIds.length > 1 ? id === mainHolderId : true,
      }));

      const addResp = await apiService.addAccountGuest(
        acc.AccountID,
        staffId,
        payload
      );

      if (!addResp.Success) {
        alert("Check-in failed");
      }

      onClose();
    } catch (err) {
      console.error(err);
      setPinError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen && !staffPinModal) return null;

  return (
    <>
      {/* MAIN ARRIVALS MODAL */}
      <div
        className="modal fade show d-block pos-modal"
        style={{ background: "rgba(0,0,0,0.45)", zIndex: 30000 }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content rounded-4 shadow-lg">
            <div className="modal-header border-0 pb-2">
              <h5 className="modal-title fw-semibold">
                Check-In – {booking?.PartyName}
              </h5>
              <button className="btn btn-light" onClick={onClose}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {/* ACCOUNT NAME */}
              <label className="fw-semibold mb-1">Account Name</label>
              <div className="position-relative mb-3" ref={dropdownRef}>
                <input
                  type="text"
                  className="form-control"
                  value={accountName}
                  placeholder="Select or type account"
                  onFocus={() => setDropdownOpen(true)}
                  onChange={(e) => {
                    setAccountName(e.target.value);
                    setDropdownOpen(true);
                  }}
                />

                {dropdownOpen && (
                  <ul
                    className="list-group position-absolute w-100 mt-1 shadow-sm"
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 10,
                    }}
                  >
                    {accounts
                      .filter((a) =>
                        a.Name.toLowerCase().includes(
                          accountName.toLowerCase()
                        )
                      )
                      .map((acc) => (
                        <li
                          key={acc.AccountID}
                          className="list-group-item list-group-item-action"
                          onClick={() => handleAccountSelect(acc)}
                        >
                          {acc.Name}
                        </li>
                      ))}

                    {accounts.every(
                      (a) =>
                        a.Name.toLowerCase() !==
                        accountName.toLowerCase()
                    ) &&
                      accountName.trim() !== "" && (
                        <li className="list-group-item text-muted small">
                          {`New account: "${accountName}"`}
                        </li>
                      )}
                  </ul>
                )}
              </div>

              {/* GUEST TABLE */}
              <h6 className="fw-semibold mb-2">Select Guests</h6>

              <div
                className="table-responsive border rounded"
                style={{ maxHeight: "350px" }}
              >
                <table className="table table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Select</th>
                      <th>Name</th>
                      <th>Account</th>
                      <th>Status</th>
                      <th>Responsible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guests.map((g) => {
                      let icon = (
                        <Repeat size={18} color="blue" />
                      );
                      let rowClass = "";

                      if (g.IsLinkedToAccount) {
                        icon = <X size={18} color="red" />;
                        rowClass = "table-danger";
                      } else if (g.CanLinkToAccount) {
                        icon = <Check size={18} color="green" />;
                        rowClass = "table-success";
                      }

                      return (
                        <tr key={g.GuestID} className={rowClass}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedGuestIds.includes(
                                g.GuestID
                              )}
                              onChange={() => toggleGuest(g.GuestID)}
                            />
                          </td>
                          <td>
                            {g.FirstName} {g.LastName}
                          </td>
                          <td>
                            {g.LinkedAccountName || "-"}
                          </td>
                          <td>{icon}</td>
                          <td>
                            {g.IsResponsible ? "✔️" : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0">
              <button
                className="btn btn-primary px-4"
                onClick={handleCheckIn}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN HOLDER MODAL */}
      {showMainHolderModal && (
        <div
          className="modal fade show d-block pos-modal"
          style={{ background: "rgba(0,0,0,0.45)", zIndex: 31000 }}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content rounded-4 shadow-lg">
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-semibold">
                  Main Account Holder
                </h5>
                <button
                  className="btn btn-light"
                  onClick={() =>
                    setShowMainHolderModal(false)
                  }
                >
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body">
                <select
                  className="form-select"
                  value={mainHolderId || ""}
                  onChange={(e) =>
                    setMainHolderId(Number(e.target.value))
                  }
                >
                  <option value="">
                    Select a guest
                  </option>
                  {guests.map((g) => (
                    <option
                      key={g.GuestID}
                      value={g.GuestID}
                    >
                      {g.FirstName} {g.LastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  className="btn btn-primary px-4"
                  disabled={!mainHolderId}
                  onClick={() => {
                    setShowMainHolderModal(false);
                    setShowDisclaimerModal(true);
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DISCLAIMER MODAL */}
      {showDisclaimerModal && (
        <div
          className="modal fade show d-block pos-modal"
          style={{ background: "rgba(0,0,0,0.45)", zIndex: 32000 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content rounded-4 shadow-lg">
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-semibold">
                  Disclaimer
                </h5>
                <button
                  className="btn btn-light"
                  onClick={() =>
                    setShowDisclaimerModal(false)
                  }
                >
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body">
                <p>
                  {`Please ensure the disclaimer is signed before proceeding.`}
                </p>
                <p className="text-muted small">
                  {`We will integrate the PDF viewer here.`}
                </p>
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  className="btn btn-primary px-4"
                  onClick={() => {
                    // Close ALL Arrivals modals before opening PIN
setShowDisclaimerModal(false);
setShowMainHolderModal(false);

// Hide main modal without calling onClose (we still need the data in memory)
const mainModal = document.querySelector(".pos-modal");
if (mainModal) mainModal.style.display = "none";

setStaffPinModal(true);
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW STAFF PIN MODAL */}
      <StaffPinModal
        isOpen={staffPinModal}
        onClose={() => setStaffPinModal(false)}
       onApproved={(staff) => {
    const staffId = staff.StaffID;

    // Restore the main modal so it can close properly
    const mainModal = document.querySelector(".pos-modal");
    if (mainModal) mainModal.style.display = "";

    handleSubmitCheckInWithStaff(staffId);
}}

      />
    </>
  );
}
